import { motion } from "framer-motion";
import { useLiveStatus } from "../hooks/useLiveStatus";
import { useCountry } from "../hooks/useCountry";
import { useLiveOverride } from "../hooks/useLiveOverride";
import { useSchedule } from "../hooks/useSchedule";
import { convertScheduleSlotForTz } from "../utils/streamTime";
import { StreamTimeBanner } from "./StreamTimeBanner";

const todayAbbr = () => new Date().toLocaleDateString("en-US", { weekday: "short" });

export const Schedule = () => {
    const { isLive } = useLiveStatus();
    const { country } = useCountry();
    const override = useLiveOverride();
    const { days: schedule, loading } = useSchedule();
    const today = todayAbbr();

    return (
        <section className="relative py-10 lg:py-14" data-testid="schedule-section">
            <div className="max-w-7xl mx-auto px-6 lg:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5 }}
                    className="mb-10 flex items-end justify-between flex-wrap gap-4"
                >
                    <div>
                        <span className="eyebrow">This week</span>
                        <h2 className="font-display text-3xl sm:text-4xl text-white mt-2">
                            Catch me <span className="gradient-text">live</span>
                        </h2>
                        <p className="text-white/55 mt-2 max-w-xl text-sm">
                            Streams kick off at <span className="text-white">11:00 AM EST</span>.{" "}
                            {country ? (
                                <span>Shown in your local time below {country.flag}.</span>
                            ) : (
                                <span>Pick a country to see local times.</span>
                            )}
                        </p>
                    </div>
                    {country ? (
                        <div className="border border-white/10 rounded-xl px-5 py-3 bg-white/[0.02]" data-testid="timezone-display">
                            <div className="eyebrow">Your timezone</div>
                            <div className="font-display text-lg text-white mt-1 flex items-center gap-2">
                                <span>{country.flag}</span>
                                <span>{country.name}</span>
                            </div>
                        </div>
                    ) : (
                        <div className="border border-white/10 rounded-xl px-5 py-3 bg-white/[0.02]" data-testid="timezone-display">
                            <div className="eyebrow">Timezone</div>
                            <div className="font-display text-lg text-white mt-1">EST · GMT-5</div>
                        </div>
                    )}
                </motion.div>

                {/* Localized banner */}
                {country && (
                    <div className="mb-6">
                        <StreamTimeBanner />
                    </div>
                )}

                {loading && schedule.length === 0 ? (
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                        {Array.from({ length: 7 }).map((_, i) => (
                            <div key={i} className="h-36 rounded-2xl border border-white/10 bg-obsidian-900/50 animate-pulse" />
                        ))}
                    </div>
                ) : (
                    <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                        {schedule.map((s, i) => {
                            const isToday = today === s.day;
                            // Override forces today's state: live → Now, offline → OFF (regardless of stored s.off)
                            const overrideOff = isToday && override === "offline";
                            const overrideLive = isToday && override === "live";
                            const dayOff = s.off || overrideOff;
                            const showLive = !dayOff && isToday && (overrideLive || isLive);
                            const local = country && !dayOff ? convertScheduleSlotForTz(country.tz, s.dayIdx) : null;

                            return (
                                <motion.div
                                    key={s.day + i}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.4, delay: i * 0.04 }}
                                    className={`relative rounded-2xl p-5 card-tilt overflow-hidden border bg-obsidian-900/60 ${
                                        dayOff
                                            ? "border-white/[0.04] opacity-50"
                                            : showLive
                                              ? "border-red-500/40 glow-border"
                                              : isToday
                                                ? "border-brand-cyan/30"
                                                : "border-white/10"
                                    }`}
                                    data-testid={`schedule-day-${s.day.toLowerCase()}`}
                                >
                                    {showLive && (
                                        <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/20 border border-red-500/40">
                                            <span className="h-1.5 w-1.5 rounded-full bg-red-500 live-dot" />
                                            <span className="text-[9px] uppercase tracking-wider text-red-300 font-bold">Now</span>
                                        </div>
                                    )}
                                    {isToday && !showLive && !dayOff && (
                                        <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-brand-cyan/10 border border-brand-cyan/30">
                                            <span className="text-[9px] uppercase tracking-wider text-brand-cyan font-bold">Today</span>
                                        </div>
                                    )}
                                    {overrideOff && (
                                        <div className="absolute top-3 right-3 px-2 py-0.5 rounded-full bg-white/5 border border-white/15">
                                            <span className="text-[9px] uppercase tracking-wider text-white/55 font-bold">Off today</span>
                                        </div>
                                    )}
                                    <div className={`eyebrow mb-2 ${isToday ? "text-brand-cyan" : ""}`}>
                                        {s.day}
                                    </div>
                                    <div className="font-display text-lg text-white mb-1" data-testid={`schedule-time-${s.day.toLowerCase()}`}>
                                        {dayOff ? "OFF" : local ? local.time : "11:00 AM"}
                                    </div>
                                    {!dayOff && local && (
                                        <div className="text-[10px] text-white/35 font-mono mb-2">11:00 AM EST</div>
                                    )}
                                    {!dayOff && (
                                        <div className="text-xs text-white/75 font-medium mb-1.5 mt-2">{s.category}</div>
                                    )}
                                    <div className="text-xs text-white/45 leading-snug">{s.note}</div>
                                </motion.div>
                            );
                        })}
                    </div>
                )}
            </div>
        </section>
    );
};
